import { watchDebounced } from '@vueuse/core';
import type { Ref } from 'vue';
import { computed, watch } from 'vue';
import { BetSlipContext } from '@/components/betSlip/betSlipContext';
import { usePriceChangeStatus } from '@/composable/usePriceChangeStatus';
import { useState } from '@/composable/useState';
import { defineContext } from '@/core/lib/defineContext';
import { i18n } from '@/core/lib/i18n';
import { formatMinDecimalPlace } from '@/core/lib/numberFormat';
import type { IDynamicMarketBetSlip } from '@/interface/IDynamicMarketBetSlip';
import { BetSlipMessagePriority } from '@/interface/enum';

const priceDecimalPlace = 2;

export const DynamicMarketTicketContext = defineContext('DynamicMarketTicketContext', (ticket: Ref<IDynamicMarketBetSlip>) => {
    const optionName = computed(() => ticket.value.display.selectionName);

    const price = computed(() => ticket.value.selection.price.value);
    const { isPriceChange, priceChangeStatus } = usePriceChangeStatus(price);
    const [oldPrice, setOldPrice] = useState(price.value);
    watch(price, (_, oldVal) => setOldPrice(oldVal));

    const priceDisplay = computed(() => formatMinDecimalPlace(price.value, priceDecimalPlace));
    const oldPriceDisplay = computed(() => formatMinDecimalPlace(oldPrice.value, priceDecimalPlace));
    const isNegativePrice = computed(() => price.value < 0);

    const { setBetSlipMessage, subscribeOdds } = BetSlipContext.inject();
    subscribeOdds();

    watchDebounced(isPriceChange, () => {
        if (!isPriceChange.value) return;
        setBetSlipMessage(i18n.t('bet_slip_error_message_odds_changed'), BetSlipMessagePriority.OddsChange);
    }, { debounce: 100 });

    return {
        optionName,
        priceDisplay,
        oldPriceDisplay,
        isPriceChange,
        priceChangeStatus,
        isNegativePrice,
    };
});
